import { useState } from 'react';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, Box, Button, Link, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import logo from '../../Images/meshLogo.svg';
import * as constants from './constants';
import { getCustomerIdFromSearch, getTenantScopedRoute } from './customerId';
import { getPrimaryButtonSx, getTextFieldSx } from './styles';

const ForgotPassword = () => {
  const theme = useTheme();
  const location = useLocation();
  const customerId = getCustomerIdFromSearch(location.search);
  const fromState = location.state?.from;
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const textFieldSx = getTextFieldSx(theme);
  const primaryButtonSx = getPrimaryButtonSx(theme);

  const handleSendCode = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      await resetPassword({ username: username.trim() });
      setStep(2);
      setSuccess(constants.FORGOT_SUCCESS_SEND_CODE);
    } catch (err) {
      setError(err.message || constants.FORGOT_ERROR_SEND_CODE);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError(constants.FORGOT_ERROR_PASSWORDS_MISMATCH);
      return;
    }

    if (newPassword.length < 8) {
      setError(constants.FORGOT_ERROR_PASSWORD_MIN_LENGTH);
      return;
    }

    setLoading(true);

    try {
      await confirmResetPassword({
        username: username.trim(),
        confirmationCode: code.trim(),
        newPassword,
      });
      setSuccess(constants.FORGOT_SUCCESS_RESET_DONE);
      setStep(3);
    } catch (err) {
      setError(err.message || constants.FORGOT_ERROR_RESET);
    } finally {
      setLoading(false);
    }
  };

  const renderFooter = () => (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        justifyContent: 'center',
      }}
    >
      <Typography
        sx={{
          color: '#797979',
          fontSize: '14px',
          fontWeight: theme.typography.fontWeightRegular,
          lineHeight: '18px',
        }}
      >
        {constants.TALK_TO_SALES_PROMPT}
      </Typography>
      <Link
        href={constants.TALK_TO_SALES_URL}
        sx={{
          color: theme.palette.primary.main,
          fontSize: '14px',
          fontWeight: theme.typography.fontWeightMedium,
          lineHeight: '20px',
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {constants.TALK_TO_SALES_LABEL}
      </Link>
    </Box>
  );

  const renderBackLink = () => (
    <Link
      color="primary"
      component={RouterLink}
      state={{ from: fromState }}
      sx={{
        fontSize: '16px',
        fontWeight: theme.typography.fontWeightMedium,
        lineHeight: '24px',
        textAlign: 'center',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
      }}
      to={getTenantScopedRoute(constants.ROUTE_LOGIN, customerId)}
    >
      {constants.FORGOT_BACK_TO_LOGIN}
    </Link>
  );

  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        justifyContent: 'center',
        minHeight: '100vh',
        px: { xs: 2, sm: 5.25 },
        py: { xs: 2, sm: 2.75 },
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.spacing(2),
          boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.15), 0px 2px 3px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          maxWidth: 555,
          minWidth: { sm: 555 },
          p: 6,
          textAlign: 'center',
          width: '100%',
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Mesh logo"
          sx={{
            height: 31,
            width: 'auto',
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            maxWidth: 459,
            width: '100%',
          }}
        >
          <Typography
            component="h1"
            sx={{
              color: theme.palette.text.primary,
              fontSize: '30px',
              fontWeight: 700,
              lineHeight: '36px',
            }}
          >
            {step === 1
              ? constants.FORGOT_HEADING_FORGOT
              : step === 2
                ? constants.FORGOT_HEADING_RESET
                : constants.FORGOT_HEADING_DONE}
          </Typography>
          {step === 1 ? (
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '16px',
                lineHeight: '24px',
              }}
            >
              {constants.FORGOT_BODY_FORGOT}
            </Typography>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? (
            <Alert severity="success" role="status">
              {success}
            </Alert>
          ) : null}

          {step === 1 ? (
            <Box component="form" onSubmit={handleSendCode} sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  component="label"
                  htmlFor="forgot-email"
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: '16px',
                    lineHeight: '24px',
                    textAlign: 'left',
                  }}
                >
                  {constants.LOGIN_EMAIL_LABEL}
                </Typography>
                <TextField
                  autoComplete="username email"
                  disabled={loading}
                  fullWidth
                  id="forgot-email"
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder={constants.LOGIN_EMAIL_PLACEHOLDER}
                  size="small"
                  sx={textFieldSx}
                  value={username}
                />
              </Box>
              <Button
                disabled={loading || !username.trim()}
                sx={{ ...primaryButtonSx, mt: 3 }}
                type="submit"
                variant="contained"
              >
                {loading
                  ? constants.FORGOT_BUTTON_SEND_LOADING
                  : constants.FORGOT_BUTTON_SEND_CODE}
              </Button>
            </Box>
          ) : null}

          {step === 2 ? (
            <Box component="form" onSubmit={handleConfirmReset} sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography component="label" htmlFor="forgot-code" sx={{ fontSize: '16px', lineHeight: '24px', textAlign: 'left' }}>
                    {constants.FORGOT_RESET_CODE_LABEL}
                  </Typography>
                  <TextField
                    autoComplete="one-time-code"
                    disabled={loading}
                    fullWidth
                    id="forgot-code"
                    onChange={(event) => setCode(event.target.value)}
                    placeholder={constants.FORGOT_RESET_CODE_PLACEHOLDER}
                    size="small"
                    sx={textFieldSx}
                    value={code}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography component="label" htmlFor="forgot-new-password" sx={{ fontSize: '16px', lineHeight: '24px', textAlign: 'left' }}>
                    {constants.FORGOT_NEW_PASSWORD_LABEL}
                  </Typography>
                  <TextField
                    autoComplete="new-password"
                    disabled={loading}
                    fullWidth
                    id="forgot-new-password"
                    onChange={(event) => setNewPassword(event.target.value)}
                    size="small"
                    sx={textFieldSx}
                    type="password"
                    value={newPassword}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography component="label" htmlFor="forgot-confirm-password" sx={{ fontSize: '16px', lineHeight: '24px', textAlign: 'left' }}>
                    {constants.FORGOT_CONFIRM_PASSWORD_LABEL}
                  </Typography>
                  <TextField
                    autoComplete="new-password"
                    disabled={loading}
                    fullWidth
                    id="forgot-confirm-password"
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    size="small"
                    sx={textFieldSx}
                    type="password"
                    value={confirmPassword}
                  />
                </Box>
              </Box>
              <Button
                disabled={loading || !code.trim() || !newPassword || !confirmPassword}
                sx={{ ...primaryButtonSx, mt: 3 }}
                type="submit"
                variant="contained"
              >
                {loading
                  ? constants.FORGOT_BUTTON_RESET_LOADING
                  : constants.FORGOT_BUTTON_RESET}
              </Button>
            </Box>
          ) : null}

          {renderBackLink()}
        </Box>
        {renderFooter()}
      </Box>
    </Box>
  );
};

export default ForgotPassword;
